import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useGroupChatContext } from '../../context/groupchatContext';
import { usePusherContext } from '../../context/pusherContext';
import { debounce } from 'lodash';
import MessageList from './messageList';
import InfiniteScroll from 'react-infinite-scroll-component';
import { FaSpinner } from 'react-icons/fa';
import { useGetLocationChatMessagesQuery, useGetPatientChatsQuery } from '../../services/chatService';
import msgSound from '../../assets/audio/pop-alert.mp3';
import useSound from 'use-sound';
import { isLoggedInUser, isPatientChatMessage } from '../../helpers/utility';

const LOCATION_CHAT_POLLING_INTERVAL = 10000;

const ChatWindow = React.memo(() => {

    const [pagination, setPagination] = useState({limit: 50, before_id: '', after_id: ''});
    const scrollRef = useRef();
    const groupChatContext = useGroupChatContext();
    const pusherContext = usePusherContext();
    const [playSound] = useSound(msgSound);
    const [scrollMessageId, setScrollMessageId] = useState(0);

    const {authData, selectedChat, selectedChatTab, messages, setMessages, pusherData, searchedMessagethread, setSearchedMessageThread, isUrgentMessage, getPendingMessages, removePendingMessage} = useMemo(() => ({
        authData: groupChatContext.authData,
        selectedChat: groupChatContext.selectedChat,
        selectedChatTab: groupChatContext.selectedChatTab,
        messages: groupChatContext.messages,
        setMessages: groupChatContext.setMessages,
        pusherData: pusherContext.pusherData,
        searchedMessagethread: groupChatContext.searchedMessagethread,
        setSearchedMessageThread: groupChatContext.setSearchedMessageThread,
        isUrgentMessage: groupChatContext.isUrgentMessage,
        getPendingMessages: groupChatContext.getPendingMessages,
        removePendingMessage: groupChatContext.removePendingMessage,
    }));

    const selectedPatientPid = selectedChat?.pid;
    const selectedLocationId = selectedChat?.location_id || selectedChat?.location?.id;
    const isLocationChatOpen = selectedChatTab === 'location_chats' && !!selectedLocationId;
    const {currentData: currentPatientMsgData, isFetching, refetch: refetchPatientMessages} = useGetPatientChatsQuery({pid: selectedPatientPid, location_id: selectedLocationId, ...pagination, urgent: isUrgentMessage}, { skip: !selectedPatientPid || !selectedLocationId, refetchOnMountOrArgChange: true });
    const {currentData: currentLocationMsgData, isLoading: isLocationMsgLoading, refetch: refetchLocationMessages} = useGetLocationChatMessagesQuery(
        {location_id: selectedLocationId, ...pagination, urgent: isUrgentMessage},
        {
            skip: !isLocationChatOpen,
            refetchOnMountOrArgChange: true,
            pollingInterval: isLocationChatOpen ? LOCATION_CHAT_POLLING_INTERVAL : 0,
        }
    );

    const mergeUniqueMessages = (primaryMessages = [], secondaryMessages = []) => {
        const seenMessageIds = new Set();
        return [...primaryMessages, ...secondaryMessages].filter((message) => {
            const messageKey = message?.id ?? message?.temp_message_id;
            if (!messageKey) {
                return true;
            }
            const normalizedMessageKey = String(messageKey);
            if (seenMessageIds.has(normalizedMessageKey)) {
                return false;
            }
            seenMessageIds.add(normalizedMessageKey);
            return true;
        });
    }

    const isMessageForSelectedChat = (message) => {
        if (!message) {
            return false;
        }
        if (selectedChatTab === 'location_chats') {
            const messageLocationId = message?.location_id || message?.location?.id;
            return `${messageLocationId}` === `${selectedLocationId}`;
        }
        return `${message?.pid}` === `${selectedChat?.pid}`;
    }
    
    useEffect(()=>{
        const msgData = selectedChatTab === 'location_chats' ? currentLocationMsgData : currentPatientMsgData;
        if(msgData){
            let msgs = msgData?.data || [];
            
            const pendingMsgs = getPendingMessages(selectedChat);
            
            if(pendingMsgs && pendingMsgs.length > 0){
                pendingMsgs.forEach(pendingMsg => {
                    const wasFound = msgs.some(m => 
                        m.id && 
                        m.message?.text === pendingMsg.message?.text && 
                        Math.abs(new Date(m.created) - Date.now()) < 60000
                    );
                    
                    if(wasFound){
                        removePendingMessage(selectedChat, pendingMsg.temp_message_id);
                    }
                });
                
                const updatedPendingMsgs = getPendingMessages(selectedChat);
                
                if(updatedPendingMsgs.length > 0){
                    const existingTempIds = new Set(msgs.map(m => m.temp_message_id).filter(Boolean));
                    const uniquePendingMsgs = updatedPendingMsgs.filter(pm => !existingTempIds.has(pm.temp_message_id));
                    msgs = [...uniquePendingMsgs, ...msgs];
                }
            }
            
            setMessages((prevMessages) => {
                let nextMsgs = msgs;
                if(pagination.before_id){
                    nextMsgs = [...(prevMessages?.data || []), ...nextMsgs];
                }
                else if(pagination.after_id){
                    nextMsgs = [...nextMsgs, ...(prevMessages?.data || [])];
                }
                const existingMessages = prevMessages?.data || [];
                const fetchedIds = new Set(nextMsgs.map((message) => message?.id).filter(Boolean).map(String));
                const socketOnlyMessages = existingMessages.filter((message) => {
                    if (!message?.id) {
                        return isMessageForSelectedChat(message);
                    }
                    return !fetchedIds.has(String(message.id)) && isMessageForSelectedChat(message);
                });
                return {
                    ...prevMessages,
                    data: mergeUniqueMessages(nextMsgs, socketOnlyMessages),
                    meta: msgData?.meta,
                };
            });
            if(scrollMessageId > 0){
                setTimeout(()=>{
                    scrollToSpecificMessage(scrollMessageId);
                    setScrollMessageId(0)
                }, 10)
            }
        }
    }, [currentPatientMsgData, currentLocationMsgData, selectedPatientPid, selectedLocationId, selectedChatTab])

    useEffect(()=>{
        if(searchedMessagethread?.thread_id){
            if(searchedMessagethread.data){
                setMessages({...messages, data: searchedMessagethread.data});
            }
            setTimeout(()=>{
                scrollToSpecificMessage(searchedMessagethread?.thread_id);
            }, 10)
        }
    }, [searchedMessagethread])

    useEffect(() => {
        setPagination({limit: 50, before_id: '', after_id: ''})
        scrollToBottom();
        setSearchedMessageThread({thread_id: '', data:[]})
        if(!selectedChat?._openedAt){
            return;
        }

        if(isPatientChatMessage(selectedChat)){
            if(selectedPatientPid && selectedLocationId){
                refetchPatientMessages();
            }
        }
        else if(selectedLocationId){
            refetchLocationMessages();
        }
    }, [selectedPatientPid, selectedLocationId, selectedChat?._openedAt])

    const scrollToSpecificMessage = (id) => {
        if(id){
            const element = document.getElementById(id);
            if(element){
                scrollRef.current.scrollTo({
                    top: element.offsetTop - 90, 
                    behavior: 'smooth'
                });
            }
        }
    }

    const scrollToBottom = () => {
        if(scrollRef.current){
            scrollRef.current.scrollTo(0, scrollRef.current.scrollHeight);
        }
    }

    useEffect(()=>{

        if(!pusherData || !selectedChat){
            return;
        }

        const isCurrentChatOpened = selectedChatTab === 'location_chats' ? selectedLocationId == pusherData.location?.id : pusherData.pid == selectedChat.pid;
        if(isCurrentChatOpened){
            scrollToBottom();
            if(!isLoggedInUser(authData, pusherData)){
                playSound();
            }
        }
    }, [pusherData])

    const handleScroll = useMemo(()=> debounce(() => {

        const container = scrollRef.current;

        // Get all spans with the same ID within the container
        // const spans = container.querySelectorAll('#msg_day');

        // Get the container's scroll position. Since the container is reversed (column-reverse), 
        // scrollTop represents the distance from the bottom of the content to the bottom of the container.
        const containerTop = container.scrollTop;
        
        if(containerTop >= 0 && containerTop <= 0.5){
            setScrollMessageId(messages.data[0]?.id);
            setPagination({...pagination, before_id: '', after_id: messages.data[0]?.id})
        }

    }, 500), [messages])

    const fetchMoreMessages = () => {
        const msgsLength = messages.data.length;
        setPagination({...pagination, after_id: '', before_id: messages.data[msgsLength -1]?.id})
    }

    return (
        <>
            {(isFetching || isLocationMsgLoading) && (
                <div className="justify-center items-center flex overflow-x-hidden overflow-y-auto absolute inset-0 outline-none focus:outline-none before:absolute before:flex before:w-full before:h-full z-[999]">
                    <FaSpinner className="animate-spin text-green-300-500 ml-1" size={40} />
                </div>
            )}

            {/* {isMsgDateDay ?
                <div className='py-2 flex absolute top-0 w-full z-10 justify-center items-center'>
                    <span className='px-2 py-1 shadow font-medium rounded-lg text-gray-400 bg-white border-gray-300'>
                        {msgDateDay}
                    </span>
                </div>
            : null} */}

            <div className='flex-1 overflow-hidden'>
                <div id="scrollableDiv" ref={scrollRef} className="flex relative p-3 border-r overflow-y-auto flex-col-reverse h-full">
                    {/*Put the scroll bar always on the bottom*/}
                    <InfiniteScroll
                        dataLength={messages.data.length}
                        next={fetchMoreMessages}
                        style={{ display: 'flex', flexDirection: 'column-reverse' }} //To put endMessage and loader to the top.
                        inverse={true}
                        onScroll={handleScroll}
                        hasMore={messages.meta.count > messages.data.length}
                        // loader={<h4 className='mt-6'>Loading...</h4>}
                        // endMessage={
                        //     <p className='text-center text-gray-400 font-small mt-7'>
                        //       <b>You have seen it all</b>
                        //     </p>
                        // }
                        scrollableTarget="scrollableDiv"
                    >
                        <MessageList messages={messages.data} />
                    </InfiniteScroll>
                </div>
            </div>
        </>
    );
});

export default ChatWindow;